/**
 * Feetype Wise Table Component
 * Displays fees grouped by fee type with regular/miscellaneous categorization
 */
import React, { useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import LoadingComponent from '../../loadingComponent';
import { generateUnAssignFeeURL } from '../../../utils/unAssignFeeNavigation';
import { generateFeeConcessionURL } from '../../../utils/feeConcessionNavigation';

/**
 * Render Feetype Wise Table
 * @param {Object} props - Component props
 * @param {Object} props.feeData - Transformed fee data with regularFees, miscellaneousFees, and totals
 * @param {boolean} props.isLoading - Loading state
 * @param {Object} props.studentData - Student data for navigation
 * @param {Function} props.navigate - React Router navigate function
 */
export const FeetypeWiseTable = ({ feeData = null, isLoading = false, studentData = null, navigate = null }) => {
    const regularDataScrollRef = useRef(null);
    const regularTotalScrollRef = useRef(null);
    const miscDataScrollRef = useRef(null);
    const miscTotalScrollRef = useRef(null);
    const grandTotalScrollRef = useRef(null);

    useEffect(() => {
        // Flag to prevent infinite scroll loop
        let isScrolling = false;

        // Synchronize all tables together
        const syncAllTablesScroll = (scrollLeft, sourceElement) => {
            if (isScrolling) return;
            isScrolling = true;

            // Sync regular data table
            const regularDataEl = regularDataScrollRef.current?.querySelector('.p-datatable-wrapper');
            if (regularDataEl && regularDataEl !== sourceElement) {
                regularDataEl.scrollLeft = scrollLeft;
            }

            // Sync miscellaneous data table
            const miscDataEl = miscDataScrollRef.current?.querySelector('.p-datatable-wrapper');
            if (miscDataEl && miscDataEl !== sourceElement) {
                miscDataEl.scrollLeft = scrollLeft;
            }

            // Sync regular total row
            const regularTotalInner = regularTotalScrollRef.current?.querySelector('div');
            if (regularTotalInner) {
                regularTotalInner.style.transform = `translateX(-${scrollLeft}px)`;
            }

            // Sync miscellaneous total row
            const miscTotalInner = miscTotalScrollRef.current?.querySelector('div');
            if (miscTotalInner) {
                miscTotalInner.style.transform = `translateX(-${scrollLeft}px)`;
            }

            // Sync grand total row
            const grandTotalInner = grandTotalScrollRef.current?.querySelector('div');
            if (grandTotalInner) {
                grandTotalInner.style.transform = `translateX(-${scrollLeft}px)`;
            }

            // Reset flag after a brief delay
            setTimeout(() => {
                isScrolling = false;
            }, 10);
        };

        // Handler for regular fees table scroll
        const handleRegularScroll = (e) => {
            syncAllTablesScroll(e.target.scrollLeft, e.target);
        };

        // Handler for miscellaneous fees table scroll
        const handleMiscScroll = (e) => {
            syncAllTablesScroll(e.target.scrollLeft, e.target);
        };

        const regularDataEl = regularDataScrollRef.current?.querySelector('.p-datatable-wrapper');
        const miscDataEl = miscDataScrollRef.current?.querySelector('.p-datatable-wrapper');

        if (regularDataEl) {
            regularDataEl.addEventListener('scroll', handleRegularScroll);
        }
        if (miscDataEl) {
            miscDataEl.addEventListener('scroll', handleMiscScroll);
        }

        return () => {
            if (regularDataEl) {
                regularDataEl.removeEventListener('scroll', handleRegularScroll);
            }
            if (miscDataEl) {
                miscDataEl.removeEventListener('scroll', handleMiscScroll);
            }
        };
    }, [feeData]);
    if (isLoading) {
        return <LoadingComponent />;
    }

    // Prepare data even if empty - always show tables with headers
    const regularFeesData = feeData?.regularFees?.map(fee => ({
        feeType: fee.feeType,
        planName: fee.planName,
        schoolName: '',
        feeAmount: fee.termWiseActualValue,
        concession: fee.concession,
        netAmount: fee.termWiseActualValue - fee.concession ,
        paidAmount: fee.amountPaid,
        balance: fee.dueAmount,
        paymentTerm: fee.paymentTerm,
        isUnassignApprovalRaised: fee.isUnassignApprovalRaised,
        actions: (fee.paymentTerm === 'One Time' || fee.isUnassignApprovalRaised) ? null : ['unassign', 'concession']
    })) || [];

    const miscellaneousFeesData = feeData?.miscellaneousFees?.map(fee => ({
        feeType: fee.feeType,
        planName: fee.planName,
        schoolName: '',
        feeAmount: fee.termWiseActualValue,
        concession: fee.concession,
        netAmount: fee.termWiseActualValue - fee.concession ,
        paidAmount: fee.amountPaid,
        balance: fee.dueAmount,
        paymentTerm: fee.paymentTerm,
        isUnassignApprovalRaised: fee.isUnassignApprovalRaised,
        actions: (fee.paymentTerm === 'One Time' || fee.isUnassignApprovalRaised) ? null : ['unassign', 'concession']
    })) || [];

    const regularFeesTotal = {
        feeType: 'Total',
        feeAmount: feeData?.regularTotal?.feeAmount || 0,
        concession: feeData?.regularTotal?.concession || 0,
        netAmount: feeData?.regularTotal?.netAmount || 0,
        paidAmount: feeData?.regularTotal?.paidAmount || 0,
        balance: feeData?.regularTotal?.balance || 0
    };

    const miscellaneousFeesTotal = {
        feeType: 'Total',
        feeAmount: feeData?.miscellaneousTotal?.feeAmount || 0,
        concession: feeData?.miscellaneousTotal?.concession || 0,
        netAmount: feeData?.miscellaneousTotal?.netAmount || 0,
        paidAmount: feeData?.miscellaneousTotal?.paidAmount || 0,
        balance: feeData?.miscellaneousTotal?.balance || 0
    };

    const grandTotal = {
        feeType: 'Grand Total',
        feeAmount: feeData?.grandTotal?.feeAmount || 0,
        concession: feeData?.grandTotal?.concession || 0,
        netAmount: feeData?.grandTotal?.netAmount || 0,
        paidAmount: feeData?.grandTotal?.paidAmount || 0,
        balance: feeData?.grandTotal?.balance || 0
    };

    const feeTypeBodyTemplate = (rowData) => {
        return (
            <div>
                <div style={{ fontFamily: 'InterMedium', color: '#374151', fontSize: '0.9375rem' }}>
                    {rowData.feeType}
                </div>
            </div>
        );
    };

    const amountBodyTemplate = (amount) => {
        return <span style={{ fontFamily: 'InterMedium', color: '#1F2937', fontSize: '0.9375rem' }}>{amount.toFixed(2)}</span>;
    };

    const concessionBodyTemplate = (rowData) => {
        return amountBodyTemplate(rowData.concession);
    };

    const paidAmountBodyTemplate = (rowData) => {
        return amountBodyTemplate(rowData.paidAmount);
    };

    const balanceBodyTemplate = (rowData) => {
        return amountBodyTemplate(rowData.balance);
    };

    const actionsBodyTemplate = (rowData) => {
        if (!rowData.actions) return null;

        const handleUnassignFeeClick = () => {
            if (navigate && studentData) {
                const orgId = localStorage.getItem('selectedOrgId') || localStorage.getItem('orgId') || '';
                const url = generateUnAssignFeeURL({
                    studentId: studentData.userId,
                    searchLevel: 2,
                    orgId: orgId
                });
                try {
                    const profileUrl = `/studentProfile?studentId=${encodeURIComponent(studentData.userId)}`;
                    window.history.pushState({}, '', profileUrl);
                } catch (e) {
                    // ignore
                }
                navigate(url);
            }
        };

        const handleRequestConcessionClick = () => {
            if (navigate && studentData) {
                const orgId = localStorage.getItem('selectedOrgId') || localStorage.getItem('orgId') || '';
                const url = generateFeeConcessionURL({
                    studentId: studentData.userId,
                    searchLevel: 2,
                    orgId: orgId
                });
                try {
                    const profileUrl = `/studentProfile?studentId=${encodeURIComponent(studentData.userId)}`;
                    window.history.pushState({}, '', profileUrl);
                } catch (e) {
                    // ignore
                }
                navigate(url);
            }
        };

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
                {rowData.actions.includes('details') && (
                    <Button
                        label="Concession Details"
                        icon="pi pi-eye"
                        className="p-button-sm"
                        style={{
                            background: '#268900',
                            border: '1px solid #268900',
                            fontSize: '0.75rem',
                            padding: '0.4rem 0.8rem',
                            whiteSpace: 'nowrap'
                        }}
                    />
                )}
                {rowData.actions.includes('unassign') && (
                    <Button
                        label="Unassign Fee"
                        className="p-button-sm"
                        onClick={handleUnassignFeeClick}
                        style={{
                            background: '#C25400',
                            border: '1px solid #C25400',
                            color: 'white',
                            fontSize: '0.75rem',
                            padding: '0.4rem 0.8rem',
                            whiteSpace: 'nowrap'
                        }}
                    />
                )}
                {rowData.actions.includes('concession') && (
                    <Button
                        label="Request For Concession"
                        className="p-button-sm"
                        onClick={handleRequestConcessionClick}
                        style={{
                            background: '#076EFF',
                            border: '1px solid #076EFF',
                            fontSize: '0.75rem',
                            padding: '0.4rem 0.8rem',
                            whiteSpace: 'nowrap'
                        }}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="tab-content feetype-wise-content">
            <div style={{
                fontFamily: 'InterSemiBold',
                fontSize: '1rem',
                color: '#1F2937',
                marginBottom: '1rem'
            }}>
                Regular Fees
            </div>

            <div className="fee-table-container card datatable-crud-demo" style={{ overflow: 'auto' }}>
                <div ref={regularDataScrollRef}>
                    <DataTable
                        value={regularFeesData}
                        className="fee-details-datatable"
                        showGridlines
                        stripedRows
                        scrollable
                        scrollHeight="flex"
                        emptyMessage="No regular fees data available"
                    >
                        <Column field="feeType" header="Fee Type" body={feeTypeBodyTemplate} style={{ minWidth: '200px' }}></Column>
                        <Column field="feeAmount" header="Fee Amount" body={(rowData) => amountBodyTemplate(rowData.feeAmount)} style={{ minWidth: '140px' }}></Column>
                        <Column field="concession" header="Concession" body={concessionBodyTemplate} style={{ minWidth: '140px' }}></Column>
                        <Column field="netAmount" header="Net Amount" body={(rowData) => amountBodyTemplate(rowData.netAmount)} style={{ minWidth: '140px' }}></Column>
                        <Column field="paidAmount" header="Paid Amount" body={paidAmountBodyTemplate} style={{ minWidth: '140px' }}></Column>
                        <Column field="balance" header="Balance" body={balanceBodyTemplate} style={{ minWidth: '140px' }}></Column>
                        <Column field="actions" header="Actions" body={actionsBodyTemplate} style={{ minWidth: '350px' }}></Column>
                    </DataTable>
                </div>

                <div ref={regularTotalScrollRef} style={{ overflow: 'hidden' }}>
                    <div style={{ width: 'max-content' }}>
                        <DataTable
                            value={[regularFeesTotal]}
                            className="fee-totals-table"
                            showGridlines
                        >
                            <Column header="" body={() => <strong>{regularFeesTotal.feeType}</strong>} style={{ minWidth: '200px', width: '200px' }} />
                            <Column header="" body={() => <strong>{regularFeesTotal.feeAmount.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                            <Column header="" body={() => <strong>{regularFeesTotal.concession.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                            <Column header="" body={() => <strong>{regularFeesTotal.netAmount.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                            <Column header="" body={() => <strong>{regularFeesTotal.paidAmount.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                            <Column header="" body={() => <strong>{regularFeesTotal.balance.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                            <Column header="" body={() => null} style={{ minWidth: '350px', width: '350px' }} />
                        </DataTable>
                    </div>
                </div>
            </div>

            {miscellaneousFeesData.length > 0 && (
                <>
                    <div style={{
                        fontFamily: 'InterSemiBold',
                        fontSize: '1rem',
                        color: '#1F2937',
                        marginTop: '2rem',
                        marginBottom: '1rem'
                    }}>
                        Miscellaneous Fees
                    </div>

                    <div className="fee-table-container card datatable-crud-demo" style={{ overflow: 'auto' }}>
                        <div ref={miscDataScrollRef}>
                            <DataTable
                                value={miscellaneousFeesData}
                                className="fee-details-datatable"
                                showGridlines
                                stripedRows
                                scrollable
                                scrollHeight="flex"
                                emptyMessage="No miscellaneous fees data available"
                            >
                                <Column field="feeType" header="Fee Type" body={feeTypeBodyTemplate} style={{ minWidth: '200px' }}></Column>
                                <Column field="feeAmount" header="Fee Amount" body={(rowData) => amountBodyTemplate(rowData.feeAmount)} style={{ minWidth: '140px' }}></Column>
                                <Column field="concession" header="Concession" body={concessionBodyTemplate} style={{ minWidth: '140px' }}></Column>
                                <Column field="netAmount" header="Net Amount" body={(rowData) => amountBodyTemplate(rowData.netAmount)} style={{ minWidth: '140px' }}></Column>
                                <Column field="paidAmount" header="Paid Amount" body={paidAmountBodyTemplate} style={{ minWidth: '140px' }}></Column>
                                <Column field="balance" header="Balance" body={balanceBodyTemplate} style={{ minWidth: '140px' }}></Column>
                                <Column field="actions" header="Actions" body={actionsBodyTemplate} style={{ minWidth: '350px' }}></Column>
                            </DataTable>
                        </div>

                        <div ref={miscTotalScrollRef} style={{ overflow: 'hidden' }}>
                            <div style={{ width: 'max-content' }}>
                                <DataTable
                                    value={[miscellaneousFeesTotal]}
                                    className="fee-totals-table"
                                    showGridlines
                                >
                                    <Column header="" body={() => <strong>{miscellaneousFeesTotal.feeType}</strong>} style={{ minWidth: '200px', width: '200px' }} />
                                    <Column header="" body={() => <strong>{miscellaneousFeesTotal.feeAmount.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                                    <Column header="" body={() => <strong>{miscellaneousFeesTotal.concession.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                                    <Column header="" body={() => <strong>{miscellaneousFeesTotal.netAmount.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                                    <Column header="" body={() => <strong>{miscellaneousFeesTotal.paidAmount.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                                    <Column header="" body={() => <strong>{miscellaneousFeesTotal.balance.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                                    <Column header="" body={() => null} style={{ minWidth: '350px', width: '350px' }} />
                                </DataTable>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <div ref={grandTotalScrollRef} style={{ overflow: 'hidden', marginTop: '0.5rem' }}>
                <div style={{ width: 'max-content' }}>
                    <DataTable
                        value={[grandTotal]}
                        className="fee-grand-total-table"
                        showGridlines
                    >
                        <Column header="" body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.feeType}</strong>} style={{ minWidth: '200px', width: '200px' }} />
                        <Column header="" body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.feeAmount.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                        <Column header="" body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.concession.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                        <Column header="" body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.netAmount.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                        <Column header="" body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.paidAmount.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                        <Column header="" body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.balance.toFixed(2)}</strong>} style={{ minWidth: '140px', width: '140px' }} />
                        <Column header="" body={() => null} style={{ minWidth: '350px', width: '350px' }} />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};
